import { createBrowserRouter, Navigate, useLocation } from "react-router";
import { RouterProvider } from "react-router/dom";
import {
  getBasePathWithoutTrailingSlash,
  isLocalDev,
  isMockApiEnabled,
} from "./common/env.ts";
import { useRef } from "react";
import { mockApiReady } from "./common/api/mockApi";
import RootErrorBoundary from "./routes/root/RootErrorBoundary.tsx";
import { homeRoutes } from "./routes/home/homeRoutes.tsx";
import {
  rootLoader,
  rootRouteId,
  shouldRevalidateRoot,
} from "./routes/root/rootLoader.ts";
import Root from "./routes/root/Root.tsx";
import DeweyIntlProvider from "./common/core-ui/DeweyIntlProvider.tsx";
import { userRoutes } from "./routes/user/userRoutes.tsx";
import { HelmetProvider } from "react-helmet-async";
import FeaturesMain from "./routes/features/FeaturesMain.tsx";
import DotLoader from "./common/core-ui/DotLoader.tsx";
import styles from "./Main.module.scss";
import EmbedMain from "./routes/embed/EmbedMain.tsx";
import { rebuildHashedRoute } from "./routes/bookshelfLinks.ts";
import ExploreDeprecated from "./routes/explore-deprecated/ExploreDeprecated.tsx";

function RootRedirect() {
  const location = useLocation();
  const destination =
    rebuildHashedRoute(location) ?? `/dashboard${location.search}`;
  return <Navigate to={destination} state={{ isRedirect: true }} />;
}

// VST-103842: createBrowserRouter fires the root loader immediately, before
// MSW has finished registering in local dev. Waiting on mockApi's readiness
// here keeps router creation at module scope (as react-router recommends)
// while avoiding that race. Dead-code-eliminated outside local dev mock mode.
if (isLocalDev() && isMockApiEnabled()) {
  await mockApiReady;
}

const router = createBrowserRouter(
  [
    {
      errorElement: <RootErrorBoundary />,
      loader: rootLoader,
      id: rootRouteId,
      shouldRevalidate: shouldRevalidateRoot,
      element: <Root />,
      hydrateFallbackElement: <DotLoader className={styles.loader} />,
      children: [
        homeRoutes,
        userRoutes,
        { path: "/explore-deprecated", element: <ExploreDeprecated /> },
        { path: "/embed/*?", element: <EmbedMain /> },
        { path: "/features", element: <FeaturesMain /> },
        {
          path: "/",
          element: <RootRedirect />,
        },
      ],
    },
  ],
  {
    basename: getBasePathWithoutTrailingSlash(),
  }
);

export default function Main() {
  const routerRef = useRef(router);
  return (
    <HelmetProvider>
      <DeweyIntlProvider>
        <RouterProvider router={routerRef.current} />
      </DeweyIntlProvider>
    </HelmetProvider>
  );
}
